import {
	ConceptDefinitionDTODataTypeEnum,
	type ConfidenceLevelEnum,
	type ParameterizedConceptDTORelativeness,
	type ParameterizedConceptDTORelativenessModeEnum,
	type ParameterizedConceptDTORepresentationModeEnum,
	type RiskModelExposureLevels,
	type SourceEnum,
	type TimeHorizonEnum,
	type ConceptDefinitionDTO,
} from "@eu.mdotm.api/smart-workspaces";
import { toClassName } from "@mdotm/shadcn/legacy/react-extensions";
import { builtInSortFnFor, dedup, typedObjectEntries, typedObjectKeys } from "@mdotm/shadcn/legacy/utils";
import {
	type HierarchicalOption,
	type HierarchicalOptions,
	HierarchicalSelect,
	Select,
	type SelectOption,
	type SelectOptions,
	type SelectProps,
	FormField,
} from "@mdotm/shadcn/mdotm/components";
import { ForEach } from "@mdotm/utils/react-extra";
import type { UseQueryResult } from "@tanstack/react-query";
import Immutable, { OrderedSet } from "immutable";
import { useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { ReactQueriesWrapperBase, ReactQueryWrapperBase } from "$root/components/ReactQueryWrapper";
import { useMappedQuery } from "$root/third-party-integrations/react-query/hooks";
import { qualifier } from "$root/utils";
import {
	comparisonChoiceId,
	comparisonChoiceLabelKey,
	comparisonChoicesOf,
	isPresenceComparison,
} from "../comparison-operator";
import { type SelectConceptConditionProps, SelectConceptConditionValue, SelectConceptParameter } from "../data-type";
import { defaultsToOwnReference, relativenessModesFor, takesRelativeness } from "../representation-mode";
import { workspaceRepository } from "../repository";
import {
	breakdownAllOption,
	riskModelDimensionForLevel,
	riskModelLevelForDimension,
	riskModelLevelLabels,
	tagBreakdownDimension,
} from "./breakdown";
import type { RichConceptDefinitionDTO } from "./type";

//TODO: discriminate WorkspaceConceptParameter based on dataType attributes

/**
 * Convert a flat list of options carrying a `group` field into the new `SelectOptions`
 * `[group, items][]` tuple shape, preserving first-seen group order and dropping the
 * per-item `group` field.
 */
/** @internal — exported for tests */
export function groupSelectOptions<T>(rows: Array<SelectOption<T> & { group: string }>): SelectOptions<T> {
	const byGroup = new Map<string, SelectOption<T>[]>();
	for (const { group, ...opt } of rows) {
		const arr = byGroup.get(group) ?? [];
		arr.push(opt as SelectOption<T>);
		byGroup.set(group, arr);
	}
	return [...byGroup.entries()];
}

/** @internal — exported for tests */
export function createConceptOptionsTree(
	concepts: ConceptDefinitionDTO[],
	metricsOnly: boolean = false,
	disabledConceptIds?: Set<string>,
): HierarchicalOptions<string> {
	const groupByTopValue = new Map<string, string>();

	function addNodeToExactPath(root: HierarchicalOption<string>[], path: HierarchicalOption<string>[]): void {
		if (path.length === 0) {
			return;
		}

		const level = path.at(0)!;
		const matchNode = root.find((node) => node.value === level.value);
		if (matchNode !== undefined) {
			matchNode.children = [...(matchNode.children ?? [])];
			addNodeToExactPath(matchNode.children, path.slice(1));
		} else {
			root.push(level);
			const matchNode = root.find((node) => node.value === level.value)!;
			matchNode.children = [...(matchNode.children ?? [])];
			addNodeToExactPath(matchNode.children, path.slice(1));
		}
	}

	function isMetric(concept: ConceptDefinitionDTO): boolean {
		return concept.dataType === "NUMERIC" || concept.dataType === "CUSTOM_ATTRIBUTE_NUMBER";
	}

	function createGroup(concept: ConceptDefinitionDTO) {
		if (
			concept.dataType === ConceptDefinitionDTODataTypeEnum.CustomAttributeDate ||
			concept.dataType === ConceptDefinitionDTODataTypeEnum.CustomAttributeText ||
			concept.dataType === ConceptDefinitionDTODataTypeEnum.CustomAttributeNumber ||
			concept.dataType === ConceptDefinitionDTODataTypeEnum.CustomAttributeTag
		) {
			return "Custom properties";
		}

		if (isMetric(concept)) {
			return "Key Metric";
		}

		return "General Portfolio Information";
	}

	function createOptions(
		concepts: ConceptDefinitionDTO[],
		tree: HierarchicalOption<string>[] = [],
	): HierarchicalOption<string>[] {
		if (concepts.length === 0) {
			return tree;
		}

		const concept = concepts.at(0)!;
		const taxonomy = concept.taxonomy?.filter((x) => x !== "Properties" && x !== "Metrics") ?? [];

		const path: Array<{ label: string; value: string; disabled?: boolean }> = [
			...taxonomy.map((t) => ({
				label: t,
				value: t,
			})),
			{
				label: concept.defaultLabel!,
				value: concept.id!,
				disabled: disabledConceptIds?.has(concept.id!),
			},
		];
		// Record the group on the top-level node's value so it can be projected into `[group, items]`
		// tuples after the tree is fully built (groups are a level concern, not a node field).
		// First-writer-wins, matching the original (a re-used top node keeps its initial group).
		if (!groupByTopValue.has(path[0].value)) {
			groupByTopValue.set(path[0].value, createGroup(concept));
		}
		addNodeToExactPath(tree, path);

		return createOptions(concepts.slice(1), tree);
	}

	// No chart-only concept to keep out of the grid and filter pickers, and none to let into the chart's:
	// a metric that can be drawn as a line is the *same* concept as the one in a column, distinguished by
	// a request-time flag rather than by a catalogue entry of its own, so every picker lists it once and
	// the plain "is it a metric" question is the whole of the filtering again.
	const tree = createOptions(metricsOnly ? concepts.filter(isMetric) : concepts);
	const groupOf = (node: HierarchicalOption<string>): string => {
		const group = groupByTopValue.get(node.value) ?? "";
		// In metrics-only mode only the "Custom properties" group keeps a heading.
		return metricsOnly && group !== "Custom properties" ? "" : group;
	};

	const byGroup = new Map<string, HierarchicalOption<string>[]>();
	for (const node of tree) {
		const group = groupOf(node);
		const items = byGroup.get(group) ?? [];
		items.push(node);
		byGroup.set(group, items);
	}
	const entries = [...byGroup.entries()];
	// A single un-named group renders flat (no heading); otherwise keep the `[group, items]` tuples.
	return entries.length === 1 && entries[0][0] === "" ? entries[0][1] : entries;
}

export const fakeTagConcept: RichConceptDefinitionDTO = {
	id: "fakeSystemId: tags",
	description: "",
	defaultLabel: "Tag",
	supportedTypes: ["INVESTMENT"],
	allowedSource: ["CURRENT", "PROPOSAL"],
	dataType: "NUMERIC",
	numericDecimalPlaces: 2,
	unitOfMeasurement: "%",
	allowedComparisonOperators: ["eq", "btw", "lt", "gt"],
	allowedTimeHorizons: [],
	allowedConfidenceLevels: [],
	representationModes: ["ABSOLUTE", "RELATIVE", "BENCHMARK_VALUE"],
	relativenessModes: ["INVESTMENT", "REFERENCE", "BENCHMARK"],
	sortable: true,
	taxonomy: ["Metrics", "Exposure"],
	availableTagsConcepts: [],
};

function SelectConcept(props: {
	value: string | null;
	onChange(
		params: {
			conceptId: string;
			concept?: RichConceptDefinitionDTO;
			fakeConceptId?: string;
		} | null,
	): void;
	metricsOnly?: boolean;
	disabled?: boolean;
	/**
	 * mainly use to disable an option or remap the label
	 */
	optionsToDisable?(concept: ConceptDefinitionDTO): boolean;
}) {
	const { metricsOnly = false } = props;
	const conceptsQuery = workspaceRepository.getConceptDefinitions.useQuery("INVESTMENT");
	const conceptsMapQuery = useMappedQuery(
		({ data: concepts }) => {
			const conceptsMap = concepts.map((concept): [string, ConceptDefinitionDTO] => [concept.id!, concept]);
			conceptsMap.push([fakeTagConcept.id!, fakeTagConcept]);
			return Immutable.Map<string, ConceptDefinitionDTO>(conceptsMap);
		},
		[],
		conceptsQuery,
	);

	const conceptsOptions = useMappedQuery(
		({ data }) => {
			const optionsWithoutName = data.filter((c) => c.id !== "systemId: uuid");
			const optionsWithoutTag = optionsWithoutName.reduce<{
				tagsConcepts: ConceptDefinitionDTO[];
				generalConcepts: ConceptDefinitionDTO[];
			}>(
				(acc, item) => {
					if (item.id?.includes("customTagId")) {
						acc.tagsConcepts.push(item);
						return acc;
					}

					acc.generalConcepts.push(item);
					return acc;
				},
				{ tagsConcepts: [], generalConcepts: [] },
			);

			const riskModelConceptIndex = optionsWithoutTag.generalConcepts.findIndex(
				(concept) => concept.id === "systemId: riskModel",
			);
			const tempRiskModelConcept = [...optionsWithoutTag.generalConcepts].at(riskModelConceptIndex)!;
			optionsWithoutTag.generalConcepts.splice(riskModelConceptIndex, 1);
			optionsWithoutTag.generalConcepts.push(fakeTagConcept);
			optionsWithoutTag.generalConcepts.push(tempRiskModelConcept);
			return {
				...optionsWithoutTag,
				tagsConcepts: optionsWithoutTag.tagsConcepts.sort(builtInSortFnFor("defaultLabel")),
			};
		},
		[],
		conceptsQuery,
	);

	return (
		<ReactQueriesWrapperBase
			queries={[conceptsOptions, conceptsMapQuery]}
			loadingFallback={<Select value={null} options={[]} disabled className="w-full mt-[18px]" />}
		>
			{([conceptOptions, conceptsMap]) => (
				<FormField label={props.metricsOnly ? "Key Metric" : "Portfolio Property"}>
					{(formFieldProps) => {
						let disabledConceptIds: Set<string> | undefined;
						if (props.optionsToDisable) {
							const canDisable = props.optionsToDisable;
							disabledConceptIds = new Set(
								conceptOptions.generalConcepts.flatMap((x) => (canDisable(x) && x.id ? [x.id] : [])),
							);
						}

						const options = createConceptOptionsTree(conceptOptions.generalConcepts, metricsOnly, disabledConceptIds);
						return (
							<HierarchicalSelect
								enableClear={props.disabled ? false : props.value != null}
								enableSearch
								placeholder={props.metricsOnly ? "Select key metric" : "Select portfolio information or key metric"}
								value={props.value}
								options={options}
								onChange={(conceptId) => {
									if (conceptId) {
										const concept = conceptsMap.get(conceptId)!;
										props.onChange({
											conceptId,
											concept:
												fakeTagConcept.id === conceptId
													? {
															...concept,
															availableTagsConcepts: conceptOptions.tagsConcepts,
														}
													: concept,
											fakeConceptId: fakeTagConcept.id === conceptId ? fakeTagConcept.id : undefined,
										});
										return;
									}
									props.onChange(null);
								}}
								{...formFieldProps}
								disabled={props.disabled}
								triggerDataAttrs={{
									"data-qualifier": qualifier.component.hierarchicalSelect.trigger("ConceptType"),
								}}
							/>
						);
					}}
				</FormField>
			)}
		</ReactQueriesWrapperBase>
	);
}

function SelectConceptTimeHorizon(props: {
	allowedTimeHorizons: TimeHorizonEnum[];
	value: TimeHorizonEnum | null;
	onChange(newValue: TimeHorizonEnum): void;
	disabled?: boolean;
}) {
	const options = props.allowedTimeHorizons.map((horizon) => ({
		value: horizon,
		label: horizon,
	}));
	return (
		<Select
			triggerDataAttrs={{ "data-qualifier": "SelectConceptTimeHorizon" }}
			value={props.value}
			options={options}
			onChange={props.onChange}
			disabled={props.disabled}
		/>
	);
}

function SelectConceptConfidence(props: {
	allowedConfidence: ConfidenceLevelEnum[];
	value: ConfidenceLevelEnum | null;
	onChange(newValue: ConfidenceLevelEnum): void;
	disabled?: boolean;
}) {
	const options = props.allowedConfidence.map((confidence) => ({
		value: confidence,
		label: confidence,
	}));
	return (
		<Select
			triggerDataAttrs={{ "data-qualifier": "SelectConceptConfidence" }}
			value={props.value}
			options={options}
			onChange={props.onChange}
			disabled={props.disabled}
		/>
	);
}

function SelectHierarchicalConceptExposureOption(props: {
	tagsConcepts: ConceptDefinitionDTO[];
	value: { id: string; opt: string } | null;
	onChange(newValue: { id: string; opt: string } | null): void;
	disabled?: boolean;
	/**
	 * Offer the {@link breakdownAllOption} leaf on every tag that declares a breakdown dimension. Off by
	 * default: only the chart chain can act on a breakdown — a filter or a table column needs the one
	 * number the leaf refuses to produce.
	 */
	allowBreakdown?: boolean;
}) {
	const { allowBreakdown } = props;
	const options = useMemo(() => {
		return props.tagsConcepts.map<HierarchicalOption<{ id: string; opt?: string }>>((concept) => ({
			label: concept.defaultLabel!,
			value: { id: concept.id! },
			children: [
				...(allowBreakdown && tagBreakdownDimension(concept)
					? [
							{
								label: allBreakdownOptionLabel,
								value: { id: concept.id!, opt: breakdownAllOption },
							},
						]
					: []),
				...(concept.exposureOptions?.map(
					(opt): HierarchicalOption<{ id: string; opt: string }> => ({
						label: opt,
						value: { id: concept.id!, opt },
					}),
				) ?? []),
			],
		}));
	}, [props.tagsConcepts, allowBreakdown]);

	return (
		<HierarchicalSelect
			enableClear
			enableSearch
			placeholder="Select tag"
			value={props.value}
			options={options}
			onChange={props.onChange}
			disabled={props.disabled}
		/>
	);
}

/** What the {@link breakdownAllOption} leaf is called wherever it is offered. */
const allBreakdownOptionLabel = "All";

function SelectConceptExposureOption(props: {
	exposureOptions: string[];
	value: string | null;
	onChange(newValue: string): void;
	disabled?: boolean;
}) {
	const options = props.exposureOptions.map((opt) => ({
		value: opt,
		label: opt,
	}));
	return (
		<Select
			triggerDataAttrs={{ "data-qualifier": "SelectConceptExposureOption" }}
			enableSearch={options.length > 10}
			value={props.value}
			options={options}
			onChange={props.onChange}
			disabled={props.disabled}
		/>
	);
}

const defaultExposureRiskModel: RiskModelExposureLevels = {
	macroAssetClass: undefined,
	microAssetClass: undefined,
	geography: undefined,
	microGeography: undefined,
	currency: undefined,
};

type AvailableRiskModelCategories = keyof RiskModelExposureLevels;
const availableRiskModelCategories = typedObjectKeys(defaultExposureRiskModel);
type ConceptRiskModel = {
	key: AvailableRiskModelCategories;
	value: string | null;
};

type SelectConceptExposureRiskModelProps = {
	conceptId: "systemId: riskModel";
	initialValue?: RiskModelExposureLevels;
	/**
	 * `breakdownDimension` is the level the user chose *not* to pin — see {@link breakdownAllOption}. It
	 * is always the last one, so `newRiskModelExposureLevels` stays the pin under it.
	 */
	onChange(newRiskModelExposureLevels?: RiskModelExposureLevels, breakdownDimension?: string): void;
	disabled?: boolean;
	/**
	 * The dimensions the concept declares breakable, verbatim from `breakdownDimensions` — backend
	 * identifiers, not level keys. Absent or empty offers no "All" leaf at all, which is what every
	 * caller but the chart chain wants.
	 */
	breakdownDimensions?: Array<string>;
	/** The dimension currently expanded, again as a backend identifier. */
	breakdownDimension?: string | null;
};

function SelectConceptExposureRiskModel(props: SelectConceptExposureRiskModelProps) {
	const availableRiskModelQuery = workspaceRepository.getConceptOptions.useQuery(props.conceptId) as UseQueryResult<
		RiskModelExposureLevels[]
	>;

	return (
		<ReactQueryWrapperBase query={availableRiskModelQuery}>
			{(options) => <SelectConceptExposureRiskModelInner {...props} options={options} />}
		</ReactQueryWrapperBase>
	);
}

function SelectConceptExposureRiskModelInner(
	props: SelectConceptExposureRiskModelProps & {
		options: RiskModelExposureLevels[];
	},
) {
	const userSelections = useMemo<Array<ConceptRiskModel>>(
		() => typedObjectEntries(props.initialValue ?? {}).flatMap(([key, value]) => (value ? [{ key, value }] : [])),
		[props.initialValue],
	);
	const breakdownDimensions = props.breakdownDimensions ?? [];
	// The breakdown always sits at the tail: it is the level the chain stops narrowing at, so offering a
	// further select after it would invite pinning something *inside* a category that is being expanded.
	const expandedLevel = props.breakdownDimension ? riskModelLevelForDimension(props.breakdownDimension) : undefined;
	const breakdownSelection: ConceptRiskModel | null = expandedLevel
		? { key: expandedLevel, value: breakdownAllOption }
		: null;
	return (
		<ForEach
			collection={breakdownSelection ? [...userSelections, breakdownSelection] : [...userSelections, null]}
			carry={{
				remainingCategories: OrderedSet(availableRiskModelCategories),
				remainingOptions: props.options,
			}}
		>
			{(item, index, carry) => {
				if (carry.remainingCategories.size === 0) {
					return null;
				}

				const pinsBefore = userSelections
					.slice(0, index)
					.flatMap(({ key, value }): Array<[AvailableRiskModelCategories, string]> => (value ? [[key, value]] : []));

				const thisSelect = (
					<HierarchicalSelect
						enableClear
						value={item}
						enableSearch
						options={createRiskModelOptions(carry.remainingOptions, carry.remainingCategories, breakdownDimensions)}
						onChange={(newRiskModelParam) => {
							if (!newRiskModelParam?.value) {
								props.onChange(Object.fromEntries(pinsBefore), undefined);
								return;
							}
							if (newRiskModelParam.value === breakdownAllOption) {
								props.onChange(Object.fromEntries(pinsBefore), riskModelDimensionForLevel(newRiskModelParam.key));
								return;
							}
							props.onChange(
								Object.fromEntries(pinsBefore.concat([[newRiskModelParam.key, newRiskModelParam.value]])),
								undefined,
							);
						}}
					/>
				);

				if (item) {
					carry.remainingCategories = carry.remainingCategories.delete(item.key);
					carry.remainingOptions =
						item.value == null || item.value === breakdownAllOption
							? carry.remainingOptions
							: carry.remainingOptions.filter((opt) => opt[item.key] === item.value);
				}

				return thisSelect;
			}}
		</ForEach>
	);
}

const riskCategoriesToLabel = riskModelLevelLabels satisfies Record<AvailableRiskModelCategories, string>;

/** @internal — exported for tests */
export function createRiskModelOptions(
	availableRiskModelOptions: RiskModelExposureLevels[],
	availableRiskModelCategories: OrderedSet<AvailableRiskModelCategories>,
	breakdownDimensions: Array<string>,
): HierarchicalOption<ConceptRiskModel>[] {
	return availableRiskModelCategories.toArray().map((category): HierarchicalOption<ConceptRiskModel> => {
		const availableRiskModelValues = dedup(availableRiskModelOptions.map((riskModel) => riskModel[category]));
		return {
			label: riskCategoriesToLabel[category],
			value: { key: category, value: null },
			children: [
				// First, because it is the level itself rather than one of its values. Matched through
				// `riskModelDimensionForLevel`: the backend advertises MACRO_GEOGRAPHY where this level is
				// keyed `geography`, so comparing the raw strings would quietly drop that level's "All".
				...(breakdownDimensions.includes(riskModelDimensionForLevel(category) ?? "")
					? [
							{
								label: allBreakdownOptionLabel,
								value: { key: category, value: breakdownAllOption },
							},
						]
					: []),
				...availableRiskModelValues.flatMap((value) =>
					value
						? [
								{
									label: value,
									value: { key: category, value },
								},
							]
						: [],
				),
			],
		};
	});
}

function SelectConceptSource(props: {
	allowedSource: SourceEnum[];
	value: SourceEnum | null;
	onChange(newValue: SourceEnum): void;
	disabled?: boolean;
}) {
	const { t } = useTranslation();
	const options = props.allowedSource.map((source) => ({
		value: source,
		label: t(`workspace.source.${source}`),
	}));
	return (
		<Select
			triggerDataAttrs={{ "data-qualifier": "SelectConceptSource" }}
			value={props.value}
			options={options}
			onChange={props.onChange}
			disabled={props.disabled}
		/>
	);
}

function SelectConceptRelativeParameters(
	props: Pick<SelectProps<ParameterizedConceptDTORelativeness>, "className" | "disabled" | "enableSearch"> & {
		allowedParameterizedConceptModes?: ParameterizedConceptDTORelativenessModeEnum[];
		conceptId: string;
		/** Adopt the "compare against each portfolio" option, when listed, instead of the first one. */
		preferOwnReference?: boolean;
	} & (
			| {
					value: ParameterizedConceptDTORelativeness | null;
					onChange(newValue: ParameterizedConceptDTORelativeness | null): void;
					multi?: false;
			  }
			| {
					value: ParameterizedConceptDTORelativeness[];
					onChange(newValue: ParameterizedConceptDTORelativeness[]): void;
					multi: true;
			  }
		),
) {
	const entitiesOptions = workspaceRepository.availableComparisonEntities.useQuery(props.conceptId);
	const mappedOptionsQuery = useMappedQuery(
		(entities) => {
			const availableEntities = entities.flatMap(
				(
					item,
				):
					| [
							SelectOption<ParameterizedConceptDTORelativeness> & {
								group: string;
							},
					  ]
					| [] => {
					if (item.name.includes("Compare against each portfolio") && item.uuid == null) {
						return [
							{
								label: item.name,
								value: {
									mode: item.type,
									uuid: `SELF_${item.type}`,
									name: item.name,
								},
								group: "",
							},
						];
					}

					return [
						{
							label: item.name,
							value: { mode: item.type, uuid: item.uuid, name: item.name },
							group: item.type,
						},
					];
				},
			);
			return availableEntities;
		},
		[],
		entitiesOptions,
	);
	// Filtered on every render, not in the mapping above, so a change of allowed modes reaches the options at once.
	const allowedOptions = mappedOptionsQuery.data?.filter((option) =>
		props.allowedParameterizedConceptModes?.includes(option.value.mode),
	);

	// biome-ignore lint/correctness/useExhaustiveDependencies: <syncs whenever the chain has no reference>
	useEffect(() => {
		if (props.value === null && allowedOptions != null && !props.multi) {
			const ownReference = props.preferOwnReference
				? allowedOptions.find((option) => option.value.uuid === `SELF_${option.value.mode}`)
				: undefined;
			const adoptedOption = ownReference ?? allowedOptions[0];
			if (adoptedOption) {
				props.onChange(adoptedOption.value);
			}
		}
	}, [allowedOptions, props.value, props.multi, props.preferOwnReference]);

	return (
		<ReactQueryWrapperBase query={mappedOptionsQuery}>
			{() => {
				const groupedOptions = groupSelectOptions(allowedOptions ?? []);
				return props.multi ? (
					<Select
						triggerDataAttrs={{
							"data-qualifier": "SelectConceptRelativeParameters",
						}}
						{...props}
						value={Array.isArray(props.value) ? props.value : []}
						options={groupedOptions}
					/>
				) : (
					<Select
						triggerDataAttrs={{
							"data-qualifier": "SelectConceptRelativeParameters",
						}}
						{...props}
						options={groupedOptions}
					/>
				);
			}}
		</ReactQueryWrapperBase>
	);
}

function SelectConceptRelativeness(props: {
	relativenessModeOptions: ParameterizedConceptDTORepresentationModeEnum[];
	relativenessMode: ParameterizedConceptDTORepresentationModeEnum | null;
	relativeness?: ParameterizedConceptDTORelativeness;
	allowedParameterizedConceptModes?: ParameterizedConceptDTORelativenessModeEnum[];
	onChangeRelativenessMode(newRelativenessMode: ParameterizedConceptDTORepresentationModeEnum): void;
	onChangeRelativenessEntity(newRelativenessEntity: ParameterizedConceptDTORelativeness | null): void;
	conceptId: string;
	disabled?: boolean;
}) {
	const { t } = useTranslation();
	const modeOptions = props.relativenessModeOptions.map((mode) => ({
		label: t(`relativeness.mode.${mode}`),
		value: mode,
	}));

	return (
		<div className="col-span-2 grid grid-cols-4 items-end gap-4">
			<FormField
				label="Metric type"
				className={{
					"col-span-2": takesRelativeness(props.relativenessMode),
					"col-span-4": props.relativenessMode === "ABSOLUTE",
				}}
			>
				{(formFieldProps) => (
					<Select
						triggerDataAttrs={{
							"data-qualifier": "SelectConceptRelativeness/MetricType",
						}}
						{...formFieldProps}
						value={props.relativenessMode}
						options={modeOptions}
						onChange={props.onChangeRelativenessMode}
						disabled={props.disabled}
					/>
				)}
			</FormField>
			{takesRelativeness(props.relativenessMode) && (
				<SelectConceptRelativeParameters
					allowedParameterizedConceptModes={relativenessModesFor(
						props.relativenessMode,
						props.allowedParameterizedConceptModes,
					)}
					preferOwnReference={defaultsToOwnReference(props.relativenessMode)}
					className={"col-span-2"}
					enableSearch
					onChange={props.onChangeRelativenessEntity}
					value={props.relativeness ?? null}
					disabled={props.disabled}
					conceptId={props.conceptId}
				/>
			)}
		</div>
	);
}

function SelectConceptCondition(props: SelectConceptConditionProps) {
	const { t } = useTranslation();
	// One entry per *choice* rather than per keyword: `exist` publishes one keyword and offers "is empty"
	// and "is not empty", which differ in the argument they stand for rather than in the keyword.
	const comparisonChoices = props.allowedComparison.flatMap(comparisonChoicesOf);
	const comparisonOptions = comparisonChoices.map((choice) => ({
		label: t(comparisonChoiceLabelKey(choice)),
		value: comparisonChoiceId(choice),
	}));
	// A presence choice is the whole condition, so the operator select takes the width the value control
	// would have had — the same span `btw` gives it over its two inputs.
	const spansTheRow = props.comparisonMode === "btw" || isPresenceComparison(props.comparisonMode);
	return (
		<div
			className={toClassName({
				"col-span-2 grid items-end gap-4": true,
				"grid-cols-4": props.comparisonMode === "btw",
				"grid-cols-2": props.comparisonMode !== "btw",
			})}
		>
			<FormField label="Condition" className={{ "col-span-2": spansTheRow }}>
				{(formFieldProps) => (
					<Select
						triggerDataAttrs={{ "data-qualifier": "SelectConceptCondition" }}
						{...formFieldProps}
						value={props.comparisonChoice ? comparisonChoiceId(props.comparisonChoice) : null}
						options={comparisonOptions}
						onChange={(newChoiceId) =>
							props.onChangeComparisonMode(
								comparisonChoices.find((choice) => comparisonChoiceId(choice) === newChoiceId) ?? null,
							)
						}
						disabled={props.disabled}
					/>
				)}
			</FormField>
			<SelectConceptConditionValue {...props} />
		</div>
	);
}

export const ChainableElement = {
	fakeTagConcept,
	SelectConcept,
	SelectConceptParameter,
	SelectConceptTimeHorizon,
	SelectConceptConfidence,
	SelectConceptSource,
	SelectConceptExposureOption,
	SelectHierarchicalConceptExposureOption,
	SelectConceptExposureRiskModel,
	SelectConceptRelativeness,
	SelectConceptCondition,
};
